import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { PhoneInput } from "react-international-phone";
import { Checkbox, ListItemText } from "@mui/material";

import "react-international-phone/style.css";

export const Login = () => {
  const navigate = useNavigate();
  const { loginWithCredentials } = useAuth();

  const [openSignup, setOpenSignup] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [localPhone, setLocalPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const initialSignupForm = {
    first_name: "",
    last_name: "",
    email: "",
    contact_number: "",
    gender: "",
    specialization: [] as string[],
    birth_date: "",
    password: "",
    primary_facility: "",
    npi: "",
  };

  const [signupForm, setSignupForm] = useState(initialSignupForm);

  const specializationList = [
    "CARDIOLOGIST",
    "DERMATOLOGIST",
    "NEUROLOGIST",
    "ORTHOPEDIC",
    "GYNECOLOGIST",
    "PHYSICIAN",
    "PEDIATRICIAN",
    "PSYCHIATRIST",
    "RADIOLOGIST",
    "DENTIST",
    "ONCOLOGIST",
    "ENT SPECIALIST",
    "GENERAL SURGEON",
    "UROLOGIST",
    "GASTROENTEROLOGIST",
    "NEPHROLOGIST",
  ];

  type SignupChangeEvent =
    | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    | SelectChangeEvent<string | string[]>;

  const handleSignupChange = (e: SignupChangeEvent) => {
    const { name, value } = e.target;

    // 👇 specialization limit check
    if (name === "specialization") {
      const selected = value as string[];

      if (selected.length > 3) {
        toast.error("You can select a maximum of 3 specializations");
        return;
      }
    }

    setSignupForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDateToDDMMYYYY = (date: string) => {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
};


  const handleChange = (value: string, meta: any) => {
    setPhone(value);

    if (meta?.country?.dialCode) {
      const dial = `+${meta.country.dialCode}`;

      setCountryCode(dial);
      setLocalPhone(value.replace(dial, ""));
    }
  };

  const validateProviderSignup = () => {
    if (!signupForm.first_name.trim()) return "First name is required";
    if (!signupForm.last_name.trim()) return "Last name is required";
    if (!signupForm.email.trim()) return "Email is required";
    if (!signupForm.password.trim()) return "Password is required";
    if (!signupForm.birth_date) return "Birth date is required";
    if (!signupForm.gender) return "Gender is required";
    if (!signupForm.specialization.length)
      return "At least one specialization is required";
    if (!countryCode) return "Country code is required";
    if (!localPhone) return "Mobile number is required";

    // optional strong checks
    if (!/^\S+@\S+\.\S+$/.test(signupForm.email))
      return "Invalid email address";

    if (signupForm.password.length < 6)
      return "Password must be at least 6 characters";

    if (localPhone.length < 8) return "Invalid mobile number";

    return null;
  };

  // ---------------------- SIGNUP PROCESS ----------------------
  const handleSignup = async () => {
    const error = validateProviderSignup();

    if (error) {
      toast.error(error);
      return;
    }

    try {
      const res = await fetch(
        "https://vivotal-backend.vercel.app/api/v1/auth/register",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...signupForm,
            birth_date: formatDateToDDMMYYYY(signupForm.birth_date),
            contact_number: localPhone,
            country_code: countryCode,
            role_type: "PROVIDER",
          }),
        }
      );

      const json = await res.json();
      if (!res.ok) {
        toast.error(json?.data?.errorMessage || "Signup failed");
        return;
      }

      toast.success("Provider registered successfully!");
      setSignupForm(initialSignupForm);
      setLocalPhone("");
      setOpenSignup(false);
    } catch (err) {
      toast.error("Signup failed");
    }
  };

  // ---------------------- LOGIN PROCESS ----------------------
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      await loginWithCredentials(email, password, "PROVIDER");
      navigate("/layout");
    } catch (err: any) {
      toast.error(err?.message || "Invalid credentials");
    }
  };

  return (
    <>
      {/* ---------------- SIGNUP POPUP ---------------- */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={openSignup}
        onClose={() => setOpenSignup(false)}
      >
        <DialogTitle>Provider Signup</DialogTitle>

        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="First Name"
              name="first_name"
              fullWidth
              value={signupForm.first_name}
              onChange={handleSignupChange}
            />

            <TextField
              label="Last Name"
              name="last_name"
              fullWidth
              value={signupForm.last_name}
              onChange={handleSignupChange}
            />

            <TextField
              label="Email"
              name="email"
              fullWidth
              value={signupForm.email}
              onChange={handleSignupChange}
            />

            <div style={{ marginTop: 6, marginBottom: 6, width: "100%" }}>
              <PhoneInput
                defaultCountry="us"
                value={phone}
                onChange={handleChange}
              />
            </div>

            {/* Birthdate */}
            <TextField
              label="Birth Date"
              name="birth_date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().split("T")[0], // 🚫 no future dates
              }}
              value={signupForm.birth_date}
              onChange={handleSignupChange}
            />

            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                label="Gender"
                name="gender"
                value={signupForm.gender}
                onChange={handleSignupChange}
              >
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Specialization</InputLabel>
              <Select
                multiple
                name="specialization"
                value={signupForm.specialization}
                onChange={handleSignupChange}
                label="Specialization"
                renderValue={(selected) => (selected as string[]).join(", ")}
              >
                {specializationList.map((sp) => {
                  const isSelected = signupForm.specialization.includes(sp);
                  const isDisabled =
                    signupForm.specialization.length >= 3 && !isSelected;

                  return (
                    <MenuItem key={sp} value={sp} disabled={isDisabled}>
                      <Checkbox checked={isSelected} />
                      <ListItemText primary={sp} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              label="Password"
              type="password"
              name="password"
              fullWidth
              value={signupForm.password}
              onChange={handleSignupChange}
            />

            <TextField
              label="primary facility"
              name="primary_facility"
              fullWidth
              value={signupForm.primary_facility}
              onChange={handleSignupChange}
            />

            <TextField
              label="NPI Number"
              name="npi"
              fullWidth
              value={signupForm.npi}
              onChange={handleSignupChange}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={() => setOpenSignup(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSignup}
            disabled={
              !signupForm.first_name ||
              !signupForm.last_name ||
              !signupForm.email ||
              !signupForm.password ||
              !signupForm.birth_date ||
              !signupForm.gender ||
              !signupForm.specialization.length ||
              !localPhone ||
              !countryCode
            }
          >
            Create Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------------- LOGIN UI ---------------------- */}
      <div className="LoginWrap">
        <div
          className="imageContainer"
          style={{
            padding: "20px",
            boxSizing: "border-box",
            color: "#FFFFFF",
          }}
        >
          <div
            style={{
              fontWeight: 500,
              fontSize: "22px",
              textAlign: "center",
            }}
          >
            With the Your Health File patient portal, your path to wellness just
            got simpler
          </div>

          <div
            style={{
              textAlign: "center",
              padding: "40px",
              boxSizing: "border-box",
            }}
          >
            <img src="bg.png" width={"100%"} alt="Decorative background" />
          </div>
        </div>

        <div className="inputField">
          <div className="inputFieldWrap">
            <div className="specialLabel">Vivotal</div>

            <div
              style={{
                marginBottom: "20px",
                fontWeight: 600,
                fontSize: "27px",
              }}
            >
              Provider Login
            </div>

            <div className="input-field">
              <label htmlFor="provider-email">Email</label>
              <input
                id="provider-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="text"
              />
            </div>

            <div className="input-field">
              <label htmlFor="provider-password">Password</label>
              <input
                id="provider-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
              />
            </div>
            <Button
              variant="contained"
              className="large-bttn"
              onClick={handleSubmit}
            >
              Sign In
            </Button>
            <div
              style={{
                textAlign: "center",
                color: "#A0AEC0",
                marginTop: 20,
                fontWeight: 600,
              }}
            >
              Don’t have an account?{" "}
              <Button
                onClick={() => setOpenSignup(true)}
                variant="text"
                sx={{
                  minWidth: 0,
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "#009FF5",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Sign up
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
